// JOBE-0160.5: W1 Roster table — sortable dense agent-row format.
//
// Sortable columns: Name, Role, Dept, Phase, Eval rate (default sort: desc),
// Tasks/wk, Latency p50.
// Row click drills to /agent/{name} with drill context (source: wcc-roster).
// Eval-rate inline <Spark mode="absolute"> for visual quality indicator.
// D10: name is a link; row click is supplementary affordance.
// D12: displayName (title-cased from fixture) shown in name column.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { StatusPill } from '../components/StatusPill/StatusPill.jsx';
import { Spark } from '../components/Spark/Spark.jsx';
import { withDrillContext } from '../lib/drillContext.js';
import { useOrganizationPeople } from '../organization/fetchOrganizationPeople.js';
import './RosterTable.css';

// Phase → StatusPill tone
const PHASE_TONE = {
  Available: 'ok',
  Degraded:  'warn',
  Disabled:  'bad',
  Draft:     'pencil',
};

// Eval rate → Spark tone
function evalTone(rate) {
  if (rate == null) return 'ink';
  if (rate >= 0.90) return 'ok';
  if (rate >= 0.75) return 'warn';
  return 'bad';
}

// Format p50 latency
function fmtLatency(ms) {
  if (ms == null) return '—';
  return `${ms}ms`;
}

// Format eval rate percentage
function fmtEval(rate) {
  if (rate == null) return '—';
  return `${(rate * 100).toFixed(0)}%`;
}

// Sort comparator factory
function makeSorter(column, direction) {
  return (a, b) => {
    let av = a[column];
    let bv = b[column];
    // Numeric columns: null values sort last
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string') {
      const cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' });
      return direction === 'asc' ? cmp : -cmp;
    }
    const cmp = av - bv;
    return direction === 'asc' ? cmp : -cmp;
  };
}

// Determine default sort direction for a column
function defaultDirection(column) {
  const NUMERIC = ['evalPassRate', 'tasksThisWeek', 'p50LatencyMs'];
  return NUMERIC.includes(column) ? 'desc' : 'asc';
}

export function RosterTable() {
  const navigate = useNavigate();
  // Reuse the W3 organization fetch — it handles the mock flag, the real
  // /api/agents + /api/departments + per-dept roster fan-out, and returns
  // unified rows. Filter to type='agent' since the roster table is
  // agent-only. Metric columns (evalPassRate, tasksThisWeek, p50LatencyMs)
  // aren't on the wire yet so they render as '—' until a richer endpoint
  // ships.
  const { rows } = useOrganizationPeople();
  const agents = rows.filter((r) => r.type === 'agent').map((r) => ({
    name: r.id,
    displayName: r.displayName,
    role: r.role,
    dept: r.dept,
    phase: r.phase,
    evalPassRate: r.evalPassRate ?? null,
    tasksThisWeek: r.tasksThisWeek ?? null,
    p50LatencyMs: r.p50LatencyMs ?? null,
    lastActiveAgo: r.lastActiveAgo ?? null,
  }));

  const [sortState, setSortState] = useState({ column: 'evalPassRate', direction: 'desc' });

  const handleHeaderClick = (column) => {
    setSortState((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: defaultDirection(column) };
    });
  };

  const sorted = [...agents].sort(makeSorter(sortState.column, sortState.direction));

  const handleRowClick = (agent) => {
    const ctx = { source: 'wcc-roster', agentName: agent.name };
    navigate(withDrillContext(`/agent/${agent.name}`, ctx));
  };

  const sortIndicator = (column) => {
    if (sortState.column !== column) return null;
    return <span className="sort-indicator" aria-hidden="true">{sortState.direction === 'asc' ? '↑' : '↓'}</span>;
  };

  const columns = [
    { key: 'displayName', label: 'Name',       sortable: true,  colKey: 'displayName' },
    { key: 'role',        label: 'Role',        sortable: true,  colKey: 'role' },
    { key: 'dept',        label: 'Dept',        sortable: true,  colKey: 'dept' },
    { key: 'phase',       label: 'Phase',       sortable: true,  colKey: 'phase' },
    { key: 'eval',        label: 'Eval rate',   sortable: true,  colKey: 'evalPassRate' },
    { key: 'tasks',       label: 'Tasks/wk',    sortable: true,  colKey: 'tasksThisWeek' },
    { key: 'latency',     label: 'Latency p50', sortable: true,  colKey: 'p50LatencyMs' },
    { key: 'lastActive',  label: 'Last active', sortable: false, colKey: 'lastActiveAgo' },
  ];

  if (!agents.length) return null;

  return (
    <div className="roster-table-wrapper">
      <table className="roster-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={col.sortable ? 'sortable-header' : ''}
                onClick={col.sortable ? () => handleHeaderClick(col.colKey) : undefined}
                aria-sort={
                  sortState.column === col.colKey
                    ? sortState.direction === 'asc' ? 'ascending' : 'descending'
                    : undefined
                }
              >
                {col.label}{sortIndicator(col.colKey)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((agent) => (
            <tr
              key={agent.name}
              className="roster-row"
              onClick={() => handleRowClick(agent)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRowClick(agent);
                }
              }}
            >
              <td>
                {/* JOBE-0158 followup: pass evalHistory array to Spark when roster endpoint provides it */}
                <Link
                  to={`/agent/${agent.name}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {agent.displayName}
                </Link>
              </td>
              <td>{agent.role}</td>
              <td>{agent.dept}</td>
              <td>
                <StatusPill
                  tone={PHASE_TONE[agent.phase] || 'pencil'}
                  label={agent.phase}
                />
              </td>
              <td className="eval-rate-cell">
                <span className="eval-rate-inner">
                  <span className="eval-rate-value">{fmtEval(agent.evalPassRate)}</span>
                  <Spark
                    mode="absolute"
                    data={[agent.evalPassRate]}
                    tone={evalTone(agent.evalPassRate)}
                    unit="%"
                    ariaLabel={`${agent.displayName} eval rate`}
                  />
                </span>
              </td>
              <td className="numeric-cell">{agent.tasksThisWeek}</td>
              <td className="numeric-cell">{fmtLatency(agent.p50LatencyMs)}</td>
              <td>{agent.lastActiveAgo}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
